import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Checkbox,
  Button,
  Typography,
  Avatar,
  FormControlLabel,
  Stack,
  Divider,
  IconButton,
  styled,
  Grid2,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useForm, Controller } from "react-hook-form";
import { lightPalette } from "../../theme-layout/palette";
import { useDispatch, useSelector } from "react-redux";
import { selectUserDetail, userDetail } from "../../redux/slice/userSlice";
import apiClient from "../../lib/api";
import urlConstant from "../../constants/urlConstants";
import SnackState from "../../shared-components/snackbar/snackState";
import CommonSnackBar from "../../shared-components/snackbar/commonSnackbar";
import formatPhoneNumber from "../../Helper/phoneNumberFormatter";
import ProfileImageModal from "./ProfileImageModal";
import { trackPromise } from "react-promise-tracker";
import Spinner from "../../shared-components/spinner";
import { IconPencil } from "@tabler/icons-react";
import dataConstant from "../../constants/dataConstants";
import {
  isValidOptionalPersonName,
  getPersonNameError,
} from "../../Helper/personNameValidation";

const AvatarContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 80,
  height: 80,
}));

const EditIconOverlay = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  right: -9,
  backgroundColor: lightPalette.primary.main,
  boxShadow: theme.shadows[1],
  "&:hover": {
    backgroundColor: lightPalette.primary.dark,
  },
}));

const ProfilePage = () => {
  const [openProfileImageDialog, setOpenProfileImageDialog] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getUserDetail = useSelector(selectUserDetail);
  const [snack, closeSnack, showSnackbar] = SnackState();
  const [apiError, setApiError] = useState(null);
  const defaultValues = {
    firstName: getUserDetail?.firstName || "",
    lastName: getUserDetail?.lastName || "",
    phone: getUserDetail?.phone || "",
    email: getUserDetail?.email || "",
    profileImageName:
      `${urlConstant.ProfileImage.ProfileImageUrl}${getUserDetail?.profileImageName}` ||
      "",
    receiveNotifications: true,
  };

  useEffect(() => {
    if (getUserDetail?.profileImageName !== "") {
      setProfileImage(
        `${urlConstant.ProfileImage.ProfileImageUrl}${getUserDetail.profileImageName}`
      );
    }
  }, [getUserDetail]);

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  const phone = watch("phone");
  useEffect(() => {
    setValue("phone", formatPhoneNumber(phone || ""));
  }, [phone]);

  const DeleteProfileImage = (userId) => {
    if (userId) {
      trackPromise(
        apiClient
          .delete(
            `${urlConstant.ProfileImage.DeleteProfileImage}?id=${userId}&loginUser=${getUserDetail?.userId}`,
            { authRequired: true }
          )
          .then((response) => {
            if (response?.statusCode === 200 && response?.status != "Failure") {
              setProfileImage(null);
              const updatedUserDetail = {
                ...getUserDetail,
                profileImageName: "",
              };
              dispatch(userDetail({ userDetail: updatedUserDetail }));
              showSnackbar(response?.message, "success");
            } else {
              showSnackbar(response?.message, "error");
              setApiError(response?.message || "Something went wrong");
            }
          })
          .catch((error) => {
            showSnackbar(error?.message, "error");
          })
      );
    }
  };

  const UpdateProfileImage = async (newProfileImageName) => {
    const payload = {
      email: getUserDetail?.email,
      profileImageName: newProfileImageName,
      loginUser: getUserDetail?.userId,
    };
    trackPromise(
      apiClient
        .post(urlConstant.ProfileImage.UpdateProfileImage, payload, {
          authRequired: true,
        })
        .then((response) => {
          if (response?.status === "Success" && response?.statusCode === 200) {
            const updateUserDetail = {
              ...getUserDetail,
              profileImageName: response?.data,
            };
            dispatch(userDetail({ userDetail: updateUserDetail }));
            showSnackbar(response?.message, "success");
          } else {
            showSnackbar(response?.message, "error");
            setApiError(response?.message || "Something went wrong");
          }
        })
    ).catch((error) => {
      showSnackbar(error?.message, "error");
    });
  };

  const handleUpdateProfileImage = (selectedProfileImageName) => {
    if (selectedProfileImageName) {
      UpdateProfileImage(selectedProfileImageName);
    } else {
      DeleteProfileImage(getUserDetail?.userId);
    }
  };

  const onSubmit = (data) => {
    const payload = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
    };
    trackPromise(
      apiClient
        .post(urlConstant.Login.UpdateProfile, payload, { authRequired: true })
        .then((response) => {
          if (response?.statusCode === 200 && response?.status !== "Failure") {
            const updatedPayload = {
              ...payload,
              roles: getUserDetail?.roles,
              userId: getUserDetail?.userId,
              profileImageName: getUserDetail?.profileImageName,
            };
            dispatch(userDetail({ userDetail: updatedPayload }));
            showSnackbar(response.message, "success");
          } else {
            setApiError(response?.message || "Something went wrong");
          }
        })
    ).catch((error) => {
      if (error.response?.data?.errors) {
        const serverErrors = error.response.data.errors;
        Object.keys(serverErrors).forEach((key) => {
          setError(key, {
            type: "server",
            message: serverErrors[key][0],
          });
        });
      } else {
        setApiError(
          error.response?.data?.message || "Failed to update profile"
        );
      }
    });
  };

  return (
    <>
      <Spinner />
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {/* Profile Header */}
          <Stack direction="row" spacing={3} alignItems="center">
            <AvatarContainer>
              <Avatar
                alt={getUserDetail?.firstName}
                src={profileImage}
                sx={{ width: "100%", height: "100%" }}
              >
                {!profileImage && `${getUserDetail?.firstName[0]}`}
              </Avatar>
              <EditIconOverlay onClick={() => setOpenProfileImageDialog(true)}>
                <IconPencil stroke={1.5} color={lightPalette.common.white} />
              </EditIconOverlay>
            </AvatarContainer>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {getUserDetail
                  ? `${getUserDetail.firstName} ${getUserDetail.lastName}`
                  : "--"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getUserDetail?.roles ?? "--"}
              </Typography>
            </Box>
          </Stack>
        </Box>

        <Typography variant="h5" mt={3}>
          Personal Information
        </Typography>

        <Grid2 container spacing={3} mt={2}>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: "First name is required",
                maxLength: {
                  value: 20,
                  message: "First name must be at most 20 characters",
                },
                validate: (name) =>
                  !name?.trim()
                    ? "First Name is required"
                    : isValidOptionalPersonName(name) ||
                      getPersonNameError("First Name"),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="First Name *"
                  fullWidth
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  inputProps={{ maxLength: 20 }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: "Last name is required",
                maxLength: {
                  value: 20,
                  message: "Last name must be at most 20 characters",
                },
                validate: (name) =>
                  !name?.trim()
                    ? "Last Name is required"
                    : isValidOptionalPersonName(name) ||
                      getPersonNameError("Last Name"),
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Last Name *"
                  fullWidth
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  inputProps={{ maxLength: 20 }}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Email" fullWidth disabled />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 6 }}>
            <Controller
              name="phone"
              control={control}
              rules={{ required: "Phone number is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone *"
                  fullWidth
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                />
              )}
            />
          </Grid2>
          <Grid2 size={{ xs: 12 }}>
            <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
              <Button onClick={() => {getUserDetail?.roles !== dataConstant.roles.marketer ? navigate('/dashboard') : navigate('/submission')}} variant="outlined" color="primary">
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </Stack>
          </Grid2>
        </Grid2>

        <ProfileImageModal
          open={openProfileImageDialog}
          onClose={() => setOpenProfileImageDialog(false)}
          onSave={handleUpdateProfileImage}
          initials={getUserDetail?.firstName}
          currentProfileImage={profileImage}
        />
        <CommonSnackBar
          snackObj={snack}
          closeSnack={closeSnack}
          duration={2000}
        />
      </Box>
    </>
  );
};

export default ProfilePage;
